import React from 'react'
import { parseUTCDate } from '@/utils/dateUtils'

const URL_REGEX = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi
const TRAILING_PUNCT = /[.,;:!?)\]}>'"]+$/

export function linkifyText(text: string): React.ReactNode {
  if (!text) return text
  const nodes: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  URL_REGEX.lastIndex = 0
  let key = 0
  while ((match = URL_REGEX.exec(text)) !== null) {
    let url = match[0]
    let start = match.index
    let end = start + url.length
    const trailing = url.match(TRAILING_PUNCT)
    if (trailing) {
      url = url.slice(0, url.length - trailing[0].length)
      end -= trailing[0].length
    }
    if (start > lastIndex) {
      nodes.push(text.slice(lastIndex, start))
    }
    const href = url.startsWith('http') ? url : `https://${url}`
    nodes.push(
      <a
        key={`u-${key++}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 underline break-all hover:text-blue-800"
        onClick={(e) => e.stopPropagation()}
      >
        {url}
      </a>,
    )
    lastIndex = end
    if (trailing) URL_REGEX.lastIndex = end
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex))
  }
  return nodes.length ? nodes : text
}

export function parseWhatsAppFormatting(text: string): React.ReactNode {
  if (!text) return null

  const parts: Array<{ text: string; format?: 'bold' | 'italic' | 'strikethrough' }> = []
  let i = 0
  let currentText = ''
  let currentFormat: 'bold' | 'italic' | 'strikethrough' | undefined

  while (i < text.length) {
    const char = text[i]
    const prevChar = i > 0 ? text[i - 1] : ''
    const nextChar = i < text.length - 1 ? text[i + 1] : ''

    if (char === '*' && nextChar !== '*' && prevChar !== '*') {
      if (currentFormat === 'bold') {
        if (currentText.trim()) {
          parts.push({ text: currentText, format: 'bold' })
          currentText = ''
          currentFormat = undefined
        } else {
          currentText += char
        }
      } else if (!currentFormat) {
        if (currentText) {
          parts.push({ text: currentText })
          currentText = ''
        }
        currentFormat = 'bold'
      } else {
        currentText += char
      }
      i++
      continue
    }

    if (char === '_' && nextChar !== '_' && prevChar !== '_') {
      if (currentFormat === 'italic') {
        if (currentText.trim()) {
          parts.push({ text: currentText, format: 'italic' })
          currentText = ''
          currentFormat = undefined
        } else {
          currentText += char
        }
      } else if (!currentFormat) {
        if (currentText) {
          parts.push({ text: currentText })
          currentText = ''
        }
        currentFormat = 'italic'
      } else {
        currentText += char
      }
      i++
      continue
    }

    if (char === '~' && nextChar !== '~' && prevChar !== '~') {
      if (currentFormat === 'strikethrough') {
        if (currentText.trim()) {
          parts.push({ text: currentText, format: 'strikethrough' })
          currentText = ''
          currentFormat = undefined
        } else {
          currentText += char
        }
      } else if (!currentFormat) {
        if (currentText) {
          parts.push({ text: currentText })
          currentText = ''
        }
        currentFormat = 'strikethrough'
      } else {
        currentText += char
      }
      i++
      continue
    }

    currentText += char
    i++
  }

  if (currentText) {
    parts.push({ text: currentText, format: currentFormat })
  }

  const rendered = parts.map((part, index) => {
    if (!part.text) return null

    const content = linkifyText(part.text)
    if (part.format === 'bold') {
      return <strong key={index}>{content}</strong>
    } else if (part.format === 'italic') {
      return <em key={index}>{content}</em>
    } else if (part.format === 'strikethrough') {
      return <del key={index}>{content}</del>
    }
    return <span key={index}>{content}</span>
  }).filter(Boolean)

  if (rendered.length === 0) {
    return <span>{linkifyText(text)}</span>
  }

  return rendered
}

export function formatMessageTime(dateString: string) {
  const date = parseUTCDate(dateString)
  if (!date) return ''
  const hours = date.getHours()
  const minutes = date.getMinutes()
  const ampm = hours >= 12 ? 'PM' : 'AM'
  const displayHours = hours % 12 || 12 // Convert to 12-hour format (0 becomes 12)
  const displayMinutes = minutes.toString().padStart(2, '0') // Always 2 digits for minutes
  
  return `${displayHours}:${displayMinutes} ${ampm}`
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}
